import {
  Bell,
  Globe,
  MessageSquare,
  Phone,
  Settings as SettingsIcon,
  Users,
  Mail,
  Smartphone,
  Monitor,
  Clock,
  Building,
  CheckCircle,
  Bot,
  UserPlus,
  Trash2,
  ShieldCheck,
  QrCode,
  Share2,
  Copy,
  ExternalLink,
  Sun,
  Moon,
  Palette,
  Lock,
} from "lucide-react";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  fetchAdminNotificationSettings,
  fetchAgentNotificationSettings,
  updateAdminNotificationSettings,
  updateAgentNotificationSettings,
} from "../../api/notificationsetting";
import {
  fetchAIPersonaSettings,
  updateAIPersonaSettings,
  fetchChatbotSettings,
  updateChatbotSettings,
} from "../../api/settings";
import { getMyAssistants, inviteAssistant, inviteAssistantById, getAssistantCandidates, removeAssistant, updateAssistantAccess } from "../../api/agent";
import { formatLeadSummaryFrequency } from "../../helper/function";
import { invalidateCache } from "../../lib/cache";
import { useTheme, THEMES } from "../../contexts/ThemeContext";
import VoiceAssistantSettings from "./VoiceAssistantSettings";
import EmailSettings from "./EmailSettings";
import TwoFactorSettings from "./TwoFactorSettings";
import SmsSendersSettings from "./SmsSendersSettings";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import AgentPortalQrModal, { buildPortalUrl } from "../../features/inspections/components/AgentPortalQrModal";

// Import UI components
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { hasAdminAccess, hasSuperAdminAccess } from "../../utils/roleAccess";

/** The three choices in Settings > General > Appearance. */
const APPEARANCE_OPTIONS = [
  {
    value: THEMES.LIGHT,
    label: "Light",
    hint: "The standard Collings look",
    Icon: Sun,
    swatch: ["#ffffff", "#f3f4f6", "#55c5d0"],
  },
  {
    value: THEMES.DARK,
    label: "Dark",
    hint: "Easier on the eyes at night",
    Icon: Moon,
    swatch: ["#161b22", "#2b333f", "#55c5d0"],
  },
  {
    value: THEMES.SYSTEM,
    label: "Match my device",
    hint: "Follows your phone or computer",
    Icon: Monitor,
    swatch: ["#ffffff", "#161b22", "#55c5d0"],
  },
];

function parseLeadSummaryFrequency(value) {
  if (!value) return 24;
  if (value === "Weekly") return 168;
  if (value === "24 hours" || value === "Daily") return 24;
  const match = value.match(/^(\d+)\s*hour/);
  if (match) return parseInt(match[1], 10);
  return 24; // default fallback
}

function mapAdminSettingsToApi(adminSettings, generalSettings) {
  return {
    new_lead_alert: adminSettings.newLeadAlertsEnabled,
    new_lead_threshold: adminSettings.newLeadThreshold,
    unassigned_lead_threshold: adminSettings.unassignedLeadThreshold,
    unassigned_lead_alert: adminSettings.unassignedLeadAlertsEnabled,
    lead_summary_frequency: parseLeadSummaryFrequency(
      adminSettings.leadSummaryFrequency
    ),
    hot_lead_priority_alert: adminSettings.hotLeadPriorityAlerts,
    new_property_alert: adminSettings.newPropertyAlertsEnabled,
    new_property_threshold: adminSettings.newPropertyThreshold,
    property_inquires_alert: adminSettings.propertyInquiries,
    listing_expirations_alert: adminSettings.listingExpirations,
    email_notifications: generalSettings.emailNotifications,
    in_app_notifications: generalSettings.inAppNotifications,
    sms_notifications: generalSettings.smsNotifications,
    push_notifications: generalSettings.pushNotifications,
    queit_start_time: generalSettings.quietHoursFrom + ":00",
    queit_end_time: generalSettings.quietHoursTo + ":00",
  };
}

function mapAgentSettingsToApi(agentSettings, generalSettings) {
  return {
    lead_summary_frequency: parseLeadSummaryFrequency(
      agentSettings.leadSummaryFrequency || "24 hours"
    ),
    follow_up_reminder_alert: parseLeadSummaryFrequency(
      agentSettings.followUpReminders
    ),
    new_lead_alert: agentSettings.newLeadAssigned,
    hot_lead_priority_alert: agentSettings.hotLeadPriorityAlert,
    property_inquires_alert: agentSettings.propertyInquiryReceived,
    virtual_tour_requests_alert: agentSettings.virtualTourRequests,
    property_market_updates:
      agentSettings.marketUpdates !== "disabled"
        ? agentSettings.marketUpdates
        : false,
    agent_handoff_alert: agentSettings.incomingCallTransfers,
    chat_requests_alert: agentSettings.chatRequests,
    call_transcripts_alert: agentSettings.callTranscriptsReady,
    profile_update_reminder: agentSettings.profileUpdateReminders,
    training_notifications_alert: agentSettings.trainingNotifications,
    performance_metrics_alert: agentSettings.performanceReviews,
    email_notifications: generalSettings.emailNotifications,
    in_app_notifications: generalSettings.inAppNotifications,
    sms_notifications: generalSettings.smsNotifications,
    push_notifications: generalSettings.pushNotifications,
    queit_start_time: generalSettings.quietHoursFrom + ":00",
    queit_end_time: generalSettings.quietHoursTo + ":00",
  };
}

const Settings = () => {
  const { user, token } = useSelector((state) => state.auth);
  // Which settings panel (and which save endpoint) this user gets. Devs are
  // mapped onto the admin panel so the tech team can see and save everything;
  // a dev matched neither branch before, so Save silently did nothing.
  const activeRole = hasAdminAccess(user) ? "admin" : (user?.role || "agent");
  // SMS Senders registers agents' mobiles with Mobile Message: super admin only
  // (Monday 13377714139). The API enforces the same gate.
  const canManageSmsSenders = hasSuperAdminAccess(user);
  // Opening Settings with ?tab=security (used by the 2FA nudge) lands straight
  // on the Security tab.
  const settingsLocation = useLocation();
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get("tab") || "general"
  );
  // Handles the case where Settings is already open and something links to
  // ?tab=security, which does not remount the page.
  useEffect(() => {
    const requested = new URLSearchParams(settingsLocation.search).get("tab");
    if (requested) setActiveTab(requested);
  }, [settingsLocation.search]);
  const [isLoading, setIsLoading] = useState(false);

  // Light / dark mode. Saved per browser, applied instantly - nothing to Save.
  const { theme, isDark, setTheme } = useTheme();

  // Portal sign-up link (every agent & admin gets a personal, attributed link)
  const [showPortalQr, setShowPortalQr] = useState(false);
  const portalSignupUrl = useMemo(() => buildPortalUrl(user?.id), [user?.id]);

  const copyPortalUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(portalSignupUrl);
      toast.success("Portal sign-up link copied.");
    } catch {
      toast.error("Could not copy the link.");
    }
  }, [portalSignupUrl]);

  /*
   * PA (Personal Assistant) state.
   *
   * Open to EVERY CRM user now, not just admins - Christian, 31 Aug 2026: "My
   * assistant is available for agents, only for admins it seems ... We need
   * agents to be able to use it" - and a user may link SEVERAL, so this is a
   * list rather than the single record it used to be.
   *
   * `paList` is null while loading and an array (possibly empty) once loaded.
   * The empty array IS the empty state; there is no separate `false`.
   */
  const [paList, setPaList] = useState(null);
  const [paMax, setPaMax] = useState(10);
  const [paLoading, setPaLoading] = useState(false);
  const [paForm, setPaForm] = useState({ name: "", email: "", phone_number: "" });
  const [paFormVisible, setPaFormVisible] = useState(false);
  const [paRemovingId, setPaRemovingId] = useState(null);
  // The assistant awaiting delete confirmation, or null. Holds the whole record
  // so the confirm dialog can name the person being removed.
  const [paPendingDelete, setPaPendingDelete] = useState(null);
  /*
   * Two ways to add one (5 Sep 2026, Monday CRM-2026-00028):
   *   'team' - pick a colleague who already works here from a dropdown. They
   *            keep their own agent login and simply assist as well, so this is
   *            an invitation they accept, not an account change.
   *   'new'  - somebody with no CRM login yet: name and email, and we create
   *            the account and email them a setup link.
   * 'team' is the default because it is what the office was trying to do when
   * typing a colleague's address overwrote their account.
   */
  const [paMode, setPaMode] = useState('team');
  const [paCandidates, setPaCandidates] = useState([]);
  const [paPickedId, setPaPickedId] = useState('');
  const [paAccessLevel, setPaAccessLevel] = useState('full_access');

  // Original settings state
  const [settings, setSettings] = useState({
    notifications: {
      newLeads: true,
      aiCallAlerts: true,
      chatAlerts: true,
      dailyReports: false,
    },
    aiDialer: {
      maxCallsPerHour: 30,
      workingHours: "9:00-17:00",
      callScript: "Hi, this is the Collings AI assistant...",
      recordCalls: true,
    },
    chatbot: {
      activeHours: "24/7",
      responseTime: "Instant",
      language: "English",
      handoffThreshold: 80,
    },
  });

  // Notification settings state
  const [adminSettings, setAdminSettings] = useState({
    newLeadAlertsEnabled: true,
    newLeadThreshold: 10,
    unassignedLeadAlertsEnabled: true,
    unassignedLeadThreshold: 5,
    leadSummaryFrequency: "24 hours",
    hotLeadPriorityAlerts: true,
    newPropertyAlertsEnabled: false,
    newPropertyThreshold: 5,
    propertyInquiries: true,
    listingExpirations: true,
  });

  const [agentSettings, setAgentSettings] = useState({
    newLeadAssigned: true,
    hotLeadPriorityAlert: true,
    followUpReminders: "24 hours",
    propertyInquiryReceived: true,
    virtualTourRequests: true,
    marketUpdates: "disabled",
    incomingCallTransfers: true,
    chatRequests: true,
    callTranscriptsReady: false,
    profileUpdateReminders: "disabled",
    trainingNotifications: true,
    performanceReviews: true,
  });

  const [generalSettings, setGeneralSettings] = useState({
    emailNotifications: true,
    inAppNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    quietHoursFrom: "22:00",
    quietHoursTo: "08:00",
  });

  // AI Persona Settings (Admin only)
  const [aiPersonaSettings, setAiPersonaSettings] = useState({
    voice_type: 'Female',
    accent: 'Australian',
    persona_name: 'Alex',
    tone: 'Professional',
    intro_script: "Hi {FirstName}, this is {PersonaName} calling from Collings Property on behalf of {AgentName}. I see you inquired about {PropertyAddress}. Is now a good time to chat?",
    is_active: true,
  });

  // Chatbot Settings (Admin only)
  const [chatbotSettings, setChatbotSettings] = useState({
    chatbot_name: 'Alex',
    welcome_message: "Hi! I'm Alex from Collings Real Estate. How can I help you today?",
    agent_availability_start: '09:00',
    agent_availability_end: '17:00',
    is_active: true,
    auto_qualify_leads: true,
    offer_calendar_booking: true,
  });

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  const handleNotificationChange = (key) => {
    setSettings((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
  };

  const updateSetting = (key, value) => {
    if (activeRole === "admin") {
      setAdminSettings((prev) => ({ ...prev, [key]: value }));
    } else {
      setAgentSettings((prev) => ({ ...prev, [key]: value }));
    }
  };

  const updateGeneralSetting = (key, value) => {
    setGeneralSettings((prev) => ({ ...prev, [key]: value }));
  };

  const currentSettings = activeRole === "admin" ? adminSettings : agentSettings;

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Save based on active tab
      if (activeTab === "general") {
        // Save AI Dialer settings from General tab (Admin only)
        if (activeRole === "admin" && settings.aiDialer) {
          // Map AI Dialer settings to AI Persona API format
          const aiDialerPayload = {
            max_calls_per_hour: parseInt(settings.aiDialer.maxCallsPerHour) || 30,
            working_hours: settings.aiDialer.workingHours,
            call_script: settings.aiDialer.callScript,
            record_calls: settings.aiDialer.recordCalls,
          };
          await updateAIPersonaSettings(token, aiDialerPayload);
          console.log('AI Dialer settings saved:', aiDialerPayload);
        }

        // Save basic Chatbot settings from General tab (Admin only)
        if (activeRole === "admin" && settings.chatbot) {
          const chatbotPayload = {
            chatbot_name: 'Alex', // Keep existing name
            active_hours: settings.chatbot.activeHours,
            response_time: settings.chatbot.responseTime,
            language: settings.chatbot.language,
            handoff_threshold: parseInt(settings.chatbot.handoffThreshold) || 80,
          };
          await updateChatbotSettings(token, chatbotPayload);
          console.log('Basic Chatbot settings saved:', chatbotPayload);
        }

        // Also save notification settings from General tab
        if (activeRole === "admin") {
          const payload = mapAdminSettingsToApi(adminSettings, generalSettings);
          await updateAdminNotificationSettings(payload);
        } else if (activeRole === "agent") {
          const payload = mapAgentSettingsToApi(agentSettings, generalSettings);
          await updateAgentNotificationSettings(payload);
        }

        toast.success("General settings updated successfully!");
      } else if (activeTab === "chatbot" && activeRole === "admin") {
        // Save Chatbot settings
        await updateChatbotSettings(token, chatbotSettings);
        toast.success("Chatbot settings updated successfully!");
      } else if (activeRole === "admin") {
        const payload = mapAdminSettingsToApi(adminSettings, generalSettings);
        await updateAdminNotificationSettings(payload);
        toast.success("Settings updated successfully!");
      } else if (activeRole === "agent") {
        const payload = mapAgentSettingsToApi(agentSettings, generalSettings);
        await updateAgentNotificationSettings(payload);
        toast.success("Settings updated successfully!");
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      if (!token) return;

      // Show loading only if it's the initial load (no cached data available)
      setIsLoading(true);

      try {
        if (activeRole === "admin") {
          // Use cached API call with stale-while-revalidate
          const data = await fetchAdminNotificationSettings({
            onStaleData: (staleData) => {
              // Immediately populate with stale data for instant UI
              setAdminSettings({
                newLeadAlertsEnabled: staleData.new_lead_alert,
                newLeadThreshold: staleData.new_lead_threshold,
                unassignedLeadAlertsEnabled: staleData?.unassigned_lead_alert,
                unassignedLeadThreshold: staleData.unassigned_lead_threshold,
                leadSummaryFrequency: formatLeadSummaryFrequency(
                  staleData.lead_summary_frequency
                ),
                hotLeadPriorityAlerts: staleData.hot_lead_priority_alert,
                newPropertyAlertsEnabled: staleData.new_property_alert,
                newPropertyThreshold: staleData.new_property_threshold,
                propertyInquiries: staleData.property_inquires_alert,
                listingExpirations: staleData.listing_expirations_alert,
              });

              setGeneralSettings({
                emailNotifications: staleData.email_notifications,
                inAppNotifications: staleData.in_app_notifications,
                smsNotifications: staleData.sms_notifications,
                pushNotifications: staleData.push_notifications,
                quietHoursFrom: staleData.queit_start_time?.slice(0, 5),
                quietHoursTo: staleData.queit_end_time?.slice(0, 5),
              });

              setIsLoading(false); // Hide loading immediately with stale data
            }
          });

          // Update with fresh data
          setAdminSettings({
            newLeadAlertsEnabled: data.new_lead_alert,
            newLeadThreshold: data.new_lead_threshold,
            unassignedLeadAlertsEnabled: data?.unassigned_lead_alert,
            unassignedLeadThreshold: data.unassigned_lead_threshold,
            leadSummaryFrequency: formatLeadSummaryFrequency(
              data.lead_summary_frequency
            ),
            hotLeadPriorityAlerts: data.hot_lead_priority_alert,
            newPropertyAlertsEnabled: data.new_property_alert,
            newPropertyThreshold: data.new_property_threshold,
            propertyInquiries: data.property_inquires_alert,
            listingExpirations: data.listing_expirations_alert,
          });

          setGeneralSettings({
            emailNotifications: data.email_notifications,
            inAppNotifications: data.in_app_notifications,
            smsNotifications: data.sms_notifications,
            pushNotifications: data.push_notifications,
            quietHoursFrom: data.queit_start_time?.slice(0, 5),
            quietHoursTo: data.queit_end_time?.slice(0, 5),
          });
        } else if (activeRole === "agent") {
          // Use cached API call with stale-while-revalidate
          const data = await fetchAgentNotificationSettings({
            onStaleData: (staleData) => {
              // Immediately populate with stale data for instant UI
              setAgentSettings({
                newLeadAssigned: staleData.new_lead_alert,
                hotLeadPriorityAlert: staleData.hot_lead_priority_alert,
                followUpReminders: formatLeadSummaryFrequency(
                  staleData.follow_up_reminder_alert
                ),
                propertyInquiryReceived: staleData.property_inquires_alert,
                virtualTourRequests: staleData.virtual_tour_requests_alert,
                marketUpdates:
                  staleData.property_market_updates === "weekly"
                    ? "weekly"
                    : staleData.property_market_updates === "monthly"
                      ? "monthly"
                      : "disabled",
                incomingCallTransfers: staleData.agent_handoff_alert,
                chatRequests: staleData.chat_requests_alert,
                callTranscriptsReady: staleData.call_transcripts_alert,
                profileUpdateReminders: staleData.profile_update_reminder,
                trainingNotifications: staleData.training_notifications_alert,
                performanceReviews: staleData.performance_metrics_alert,
              });

              setGeneralSettings({
                emailNotifications: staleData.email_notifications,
                inAppNotifications: staleData.in_app_notifications,
                smsNotifications: staleData.sms_notifications,
                pushNotifications: staleData.push_notifications,
                quietHoursFrom: staleData.queit_start_time?.slice(0, 5),
                quietHoursTo: staleData.queit_end_time?.slice(0, 5),
              });

              setIsLoading(false); // Hide loading immediately with stale data
            }
          });

          // Update with fresh data
          setAgentSettings({
            newLeadAssigned: data.new_lead_alert,
            hotLeadPriorityAlert: data.hot_lead_priority_alert,
            followUpReminders: formatLeadSummaryFrequency(
              data.follow_up_reminder_alert
            ),
            propertyInquiryReceived: data.property_inquires_alert,
            virtualTourRequests: data.virtual_tour_requests_alert,
            marketUpdates:
              data.property_market_updates === "weekly"
                ? "weekly"
                : data.property_market_updates === "monthly"
                  ? "monthly"
                  : "disabled",
            incomingCallTransfers: data.agent_handoff_alert,
            chatRequests: data.chat_requests_alert,
            callTranscriptsReady: data.call_transcripts_alert,
            profileUpdateReminders: data.profile_update_reminder,
            trainingNotifications: data.training_notifications_alert,
            performanceReviews: data.performance_metrics_alert,
          });

          setGeneralSettings({
            emailNotifications: data.email_notifications,
            inAppNotifications: data.in_app_notifications,
            smsNotifications: data.sms_notifications,
            pushNotifications: data.push_notifications,
            quietHoursFrom: data.queit_start_time?.slice(0, 5),
            quietHoursTo: data.queit_end_time?.slice(0, 5),
          });
        }

        // Fetch AI Persona and Chatbot settings (Admin only) with caching
        if (activeRole === "admin") {
          try {
            const aiPersonaData = await fetchAIPersonaSettings(token, {
              onStaleData: (staleData) => {
                setAiPersonaSettings({
                  voice_type: staleData.voice_type,
                  accent: staleData.accent,
                  persona_name: staleData.persona_name,
                  tone: staleData.tone,
                  intro_script: staleData.intro_script,
                  is_active: staleData.is_active,
                });
              }
            });

            setAiPersonaSettings({
              voice_type: aiPersonaData.voice_type,
              accent: aiPersonaData.accent,
              persona_name: aiPersonaData.persona_name,
              tone: aiPersonaData.tone,
              intro_script: aiPersonaData.intro_script,
              is_active: aiPersonaData.is_active,
            });
          } catch (error) {
            console.error('Failed to fetch AI Persona settings:', error);
          }

          try {
            const chatbotData = await fetchChatbotSettings(token, {
              onStaleData: (staleData) => {
                setChatbotSettings({
                  chatbot_name: staleData.chatbot_name,
                  welcome_message: staleData.welcome_message,
                  agent_availability_start: staleData.agent_availability_start?.slice(0, 5) || '09:00',
                  agent_availability_end: staleData.agent_availability_end?.slice(0, 5) || '17:00',
                  is_active: staleData.is_active,
                  auto_qualify_leads: staleData.auto_qualify_leads,
                  offer_calendar_booking: staleData.offer_calendar_booking,
                });
              }
            });

            setChatbotSettings({
              chatbot_name: chatbotData.chatbot_name,
              welcome_message: chatbotData.welcome_message,
              agent_availability_start: chatbotData.agent_availability_start?.slice(0, 5) || '09:00',
              agent_availability_end: chatbotData.agent_availability_end?.slice(0, 5) || '17:00',
              is_active: chatbotData.is_active,
              auto_qualify_leads: chatbotData.auto_qualify_leads,
              offer_calendar_booking: chatbotData.offer_calendar_booking,
            });
          } catch (error) {
            console.error('Failed to fetch Chatbot settings:', error);
          }
        }
      } catch (error) {
        toast.error(`Failed to fetch ${activeRole} settings`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [activeRole, token]);

  // Separate, independent fetch for PA data â€” runs when the tab becomes active.
  // No role check: every CRM user can run assistants now.
  useEffect(() => {
    // Each time the my-assistant tab is opened, re-fetch to show fresh state
    if (activeTab !== 'my-assistant') return;
    let cancelled = false;
    const fetchPA = async () => {
      setPaList(null); // show spinner while loading
      try {
        const res = await getMyAssistants();
        if (cancelled) return;
        const data = res.data;
        // Tolerate the old single-record shape so the tab still works against a
        // backend that has not been redeployed yet.
        if (Array.isArray(data?.assistants)) {
          setPaList(data.assistants);
          if (data.max) setPaMax(data.max);
        } else if (data && data.id) {
          setPaList([data]);
        } else {
          setPaList([]);
        }
      } catch (e) {
        // The old backend 404s when there is no PA, which is not an error here.
        if (!cancelled) setPaList([]);
      }
    };
    fetchPA();

    // The team dropdown. Loaded alongside the list so the picker is ready the
    // moment the form is opened. An older backend has no such endpoint, in which
    // case the form falls back to name and email only.
    const fetchCandidates = async () => {
      try {
        const res = await getAssistantCandidates();
        if (!cancelled) setPaCandidates(res.data?.candidates || []);
      } catch (e) {
        if (!cancelled) setPaCandidates([]);
      }
    };
    fetchCandidates();

    return () => { cancelled = true; };
  }, [activeTab]);

  const refreshAssistants = async () => {
    try {
      const [list, candidates] = await Promise.all([
        getMyAssistants(),
        getAssistantCandidates().catch(() => ({ data: { candidates: [] } })),
      ]);
      if (Array.isArray(list.data?.assistants)) setPaList(list.data.assistants);
      setPaCandidates(candidates.data?.candidates || []);
    } catch (e) {
      // Leave what is on screen; the next tab open re-reads it.
    }
  };

  const handleDeletePA = async () => {
    const target = paPendingDelete;
    if (!target) return;
    setPaRemovingId(target.id);
    try {
      await removeAssistant(target.id);
      setPaList((prev) => (prev || []).filter((a) => a.id !== target.id));
      setPaPendingDelete(null);
      toast.success(`${target.name || 'Personal Assistant'} removed.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove PA.");
    } finally {
      setPaRemovingId(null);
    }
  };

  const handlePaAccessLevel = async (id, newLevel) => {
    try {
      const res = await updateAssistantAccess(id, newLevel);
      setPaList((prev) => (prev || []).map((a) => (a.id === id ? res.data : a)));
      toast.success(newLevel === 'full_access' ? 'PA now has Full Access.' : 'PA access limited to Properties Only.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update access level.');
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Main Content. min-w-0 lets this column shrink to the screen so a tab
          bar wider than the screen scrolls in its own strip instead of
          stretching the whole page (which the layout then clipped). */}
      <div className="flex-1 min-w-0">
        <div className="max-w-9xl mx-auto">
          <div className="mb-4 sm:mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-2">
              <SettingsIcon className="w-5 sm:w-6 h-5 sm:h-6 text-gray-600" />
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Settings</h1>
              <Badge variant="outline" className="capitalize text-xs sm:text-sm">
                {activeRole}
              </Badge>
              {isLoading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#55C5D0]"></div>
                  <span className="text-xs sm:text-sm text-gray-500">Loading...</span>
                </div>
              )}
            </div>
            <p className="text-sm sm:text-base text-gray-600">
              Configure system preferences and notification settings
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="mb-4 sm:mb-6">
            <div className="border-b border-gray-200 overflow-x-auto">
              <nav className="-mb-px flex space-x-4 sm:space-x-8">
                <button
                  onClick={() => setActiveTab("general")}
                  className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === "general"
                    ? "border-[#55C5D0] text-[#55C5D0]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  General
                </button>
                <button
                  onClick={() => setActiveTab("notifications")}
                  className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === "notifications"
                    ? "border-[#55C5D0] text-[#55C5D0]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  Notifications
                </button>

                {/* Security Tab (all roles) */}
                <button
                  onClick={() => setActiveTab("security")}
                  className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === "security"
                    ? "border-[#55C5D0] text-[#55C5D0]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Lock className="w-3 sm:w-4 h-3 sm:h-4" />
                  <span>Security</span>
                </button>

                {/* Portal Link Tab (all roles) */}
                <button
                  onClick={() => setActiveTab("portal-link")}
                  className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === "portal-link"
                    ? "border-[#55C5D0] text-[#55C5D0]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Share2 className="w-3 sm:w-4 h-3 sm:h-4" />
                  <span className="hidden sm:inline">Portal Link</span>
                  <span className="sm:hidden">Link</span>
                </button>

                {/* Email Tab (Admin only) */}
                {activeRole === "admin" && (
                  <button
                    onClick={() => setActiveTab("email")}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === "email"
                      ? "border-[#55C5D0] text-[#55C5D0]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    <Mail className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span>Email</span>
                  </button>
                )}

                {/* SMS Senders Tab (Super admin only) */}
                {canManageSmsSenders && (
                  <button
                    onClick={() => setActiveTab("sms-senders")}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === "sms-senders"
                      ? "border-[#55C5D0] text-[#55C5D0]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    <Smartphone className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span className="hidden sm:inline">SMS Senders</span>
                    <span className="sm:hidden">SMS</span>
                  </button>
                )}

                {/* Chatbot Tab (Admin only) */}
                {activeRole === "admin" && (
                  <button
                    onClick={() => setActiveTab("chatbot")}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === "chatbot"
                      ? "border-[#55C5D0] text-[#55C5D0]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    <MessageSquare className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span className="hidden sm:inline">Website Chat</span>
                    <span className="sm:hidden">Chat</span>
                  </button>
                )}

                {/* Voice Assistant Tab (Admin only) */}
                {activeRole === "admin" && (
                  <button
                    onClick={() => setActiveTab("voice-assistant")}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === "voice-assistant"
                      ? "border-[#55C5D0] text-[#55C5D0]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    <Bot className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span className="hidden sm:inline">Voice Calling</span>
                    <span className="sm:hidden">Voice</span>
                  </button>
                )}

                {/* My Assistant Tab â€” every CRM user, not just admins
                    (Christian, 31 Aug 2026: "We need agents to be able to use it") */}
                {(
                  <button
                    onClick={() => setActiveTab("my-assistant")}
                    className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === "my-assistant"
                      ? "border-[#55C5D0] text-[#55C5D0]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    <ShieldCheck className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span className="hidden sm:inline">My Assistant</span>
                    <span className="sm:hidden">PA</span>
                  </button>
                )}
              </nav>
            </div>
          </div>

          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0]"></div>
                <p className="text-gray-600">Loading settings...</p>
              </div>
            </div>
          )}

          {/* General Settings Tab */}
          {activeTab === "general" && (
            <div className="space-y-6">
              {/* Appearance - light / dark mode (Monday 12618130483) */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-[#55C5D0]" />
                    <CardTitle>Appearance</CardTitle>
                  </div>
                  <CardDescription>
                    Choose how the CRM looks on this device. The change applies straight
                    away, nothing to save.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {APPEARANCE_OPTIONS.map(({ value, label, hint, Icon, swatch }) => {
                      const selected = theme === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setTheme(value)}
                          aria-pressed={selected}
                          className={`p-4 rounded-lg border-2 text-left transition-colors ${selected
                            ? "border-[#55C5D0] bg-[#f0fafb]"
                            : "border-gray-200 bg-white hover:border-[#55C5D0]"
                            }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Icon
                              className={`w-5 h-5 ${selected ? "text-[#55C5D0]" : "text-gray-400"}`}
                            />
                            {selected && (
                              <CheckCircle className="w-4 h-4 text-[#55C5D0]" />
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-900 text-sm">{label}</h3>
                          <p className="text-xs text-gray-600 mt-1">{hint}</p>
                          <div className="flex gap-1 mt-3">
                            {swatch.map((colour) => (
                              <span
                                key={colour}
                                className="w-5 h-3 rounded-sm border border-gray-300"
                                style={{ backgroundColor: colour }}
                              />
                            ))}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-900">Dark mode</Label>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {theme === THEMES.SYSTEM
                          ? `Following your device, currently ${isDark ? "dark" : "light"}. Flicking this switch picks a mode yourself.`
                          : "Quick on/off switch for the same setting."}
                      </p>
                    </div>
                    <Switch
                      checked={isDark}
                      onCheckedChange={(on) => setTheme(on ? THEMES.DARK : THEMES.LIGHT)}
                      aria-label="Dark mode"
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    This is your own setting, on this browser only. It does not change
                    anything your buyers or vendors see - reports, contracts and portal
                    links stay light.
                  </p>
                </CardContent>
              </Card>

              {/* Quick Links to Other Settings */}
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-lg">Settings Overview</CardTitle>
                  <CardDescription>
                    Navigate to specific settings sections below
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <button
                      onClick={() => setActiveTab("notifications")}
                      className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-[#55C5D0] transition-colors text-left group"
                    >
                      <Bell className="w-6 h-6 text-[#55C5D0] mb-2" />
                      <h3 className="font-semibold text-gray-900 group-hover:text-[#55C5D0]">
                        Notifications
                      </h3>
                      <p className="text-xs text-gray-600 mt-1">
                        Email, SMS, push alerts
                      </p>
                    </button>

                    {activeRole === "admin" && (
                      <>
                        <button
                          onClick={() => setActiveTab("email")}
                          className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-[#55C5D0] transition-colors text-left group"
                        >
                          <Mail className="w-6 h-6 text-[#55C5D0] mb-2" />
                          <h3 className="font-semibold text-gray-900 group-hover:text-[#55C5D0]">
                            Email
                          </h3>
                          <p className="text-xs text-gray-600 mt-1">
                            Turn email types on or off
                          </p>
                        </button>

                        <button
                          onClick={() => setActiveTab("chatbot")}
                          className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-[#55C5D0] transition-colors text-left group"
                        >
                          <MessageSquare className="w-6 h-6 text-[#55C5D0] mb-2" />
                          <h3 className="font-semibold text-gray-900 group-hover:text-[#55C5D0]">
                            Website Chatbot
                          </h3>
                          <p className="text-xs text-gray-600 mt-1">
                            Chat assistant for website
                          </p>
                        </button>

                        <button
                          onClick={() => setActiveTab("voice-assistant")}
                          className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-[#55C5D0] transition-colors text-left group"
                        >
                          <Bot className="w-6 h-6 text-[#55C5D0] mb-2" />
                          <h3 className="font-semibold text-gray-900 group-hover:text-[#55C5D0]">
                            Voice Assistant
                          </h3>
                          <p className="text-xs text-gray-600 mt-1">
                            AI phone calling system
                          </p>
                        </button>

                        <div className="p-4 bg-white rounded-lg border-2 border-gray-200 opacity-60">
                          <Globe className="w-6 h-6 text-gray-400 mb-2" />
                          <h3 className="font-semibold text-gray-900">
                            API Integration
                          </h3>
                          <p className="text-xs text-gray-600 mt-1">
                            Coming soon
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* System Information */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <SettingsIcon className="w-5 h-5 text-[#55C5D0]" />
                    <CardTitle>System Information</CardTitle>
                  </div>
                  <CardDescription>
                    General system settings and information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Role</p>
                      <p className="text-lg font-semibold text-gray-900 capitalize">
                        {activeRole}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Account Status</p>
                      <p className="text-lg font-semibold text-green-600">Active</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">Where to Find Settings:</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Bell className="w-4 h-4 text-[#55C5D0] mt-0.5 flex-shrink-0" />
                        <span>
                          <strong>Notifications:</strong> Configure email, SMS, and in-app alerts
                        </span>
                      </li>
                      {activeRole === "admin" && (
                        <>
                          <li className="flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-[#55C5D0] mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Chatbot:</strong> Configure website chat assistant (name, hours, welcome message)
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Bot className="w-4 h-4 text-[#55C5D0] mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Voice Assistant:</strong> Manage AI phone calling (scripts, voices, behavior)
                            </span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="space-y-6 relative">
              {/* Admin Settings */}
              {activeRole === "admin" && (
                <>
                  {/* Lead Management */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#55C5D0]" />
                        <CardTitle>Lead Management</CardTitle>
                      </div>
                      <CardDescription>
                        Configure lead-related notifications and thresholds
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-6">
                        {/* New Lead Alerts */}
                        <div className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <Label htmlFor="newLeadAlertsEnabled" className="font-medium">
                                New Lead Alerts
                              </Label>
                              <p className="text-sm text-gray-500">
                                Get notified about new leads in batches
                              </p>
                            </div>
                            <Switch
                              id="newLeadAlertsEnabled"
                              checked={currentSettings.newLeadAlertsEnabled}
                              onCheckedChange={(checked) =>
                                updateSetting("newLeadAlertsEnabled", checked)
                              }
                            />
                          </div>

                          {currentSettings.newLeadAlertsEnabled && (
                            <div className="border-t pt-4">
                              <Label htmlFor="newLeadThreshold">Alert me every</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  id="newLeadThreshold"
                                  type="number"
                                  min="1"
                                  max="50"
                                  value={currentSettings.newLeadThreshold}
                                  onChange={(e) =>
                                    updateSetting("newLeadThreshold", Number.parseInt(e.target.value))
                                  }
                                  className="min-w-20 w-fit"
                                />
                                <span className="text-sm text-gray-600">new leads</span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Unassigned Lead Alerts */}
                        <div className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <Label htmlFor="unassignedLeadAlertsEnabled" className="font-medium">
                                Unassigned Lead Alerts
                              </Label>
                              <p className="text-sm text-gray-500">
                                Alert when leads remain unassigned too long
                              </p>
                            </div>
                            <Switch
                              id="unassignedLeadAlertsEnabled"
                              checked={currentSettings.unassignedLeadAlertsEnabled}
                              onCheckedChange={(checked) =>
                                updateSetting("unassignedLeadAlertsEnabled", checked)
                              }
                            />
                          </div>

                          {currentSettings.unassignedLeadAlertsEnabled && (
                            <div className="border-t pt-4">
                              <Label htmlFor="unassignedLeadThreshold">Alert when</Label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  id="unassignedLeadThreshold"
                                  type="number"
                                  min="1"
                                  max="20"
                                  value={currentSettings.unassignedLeadThreshold}
                                  onChange={(e) =>
                                    updateSetting("unassignedLeadThreshold", Number.parseInt(e.target.value))
                                  }
                                  className="min-w-20 w-fit"
                                />
                                <span className="text-sm text-gray-600">or more leads are unassigned for 2+ hours</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label htmlFor="hotLeadPriorityAlerts" className="font-medium">
                                Hot Lead Priority Alerts
                              </Label>
                              <p className="text-sm text-gray-500">
                                When AI dialer classifies a lead as "Hot"
                              </p>
                              <div className="flex gap-2 mt-1">
                                <Badge variant="destructive">Critical</Badge>
                              </div>
                            </div>
                            <Switch
                              id="hotLeadPriorityAlerts"
                              checked={currentSettings.hotLeadPriorityAlerts}
                              onCheckedChange={(checked) =>
                                updateSetting("hotLeadPriorityAlerts", checked)
                              }
                            />
                          </div>
                        </div>

                        <Separator />

                        <div>
                          <Label htmlFor="leadSummaryFrequency">Lead Summary Report Frequency</Label>
                          <Select
                            value={currentSettings.leadSummaryFrequency}
                            onValueChange={(value) => updateSetting("leadSummaryFrequency", value)}
                          >
                            <SelectTrigger className="mt-1 max-w-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1 hour">Every hour</SelectItem>
                              <SelectItem value="3 hours">Every 3 hours</SelectItem>
                              <SelectItem value="6 hours">Every 6 hours</SelectItem>
                              <SelectItem value="12 hours">Every 12 hours</SelectItem>
                              <SelectItem value="24 hours">Daily</SelectItem>
                              <SelectItem value="Weekly">Weekly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Property Management */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Building className="w-5 h-5 text-[#55C5D0]" />
                        <CardTitle>Property Management</CardTitle>
                      </div>
                      <CardDescription>
                        Configure property-related notifications and reports
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <Label htmlFor="newPropertyAlertsEnabled" className="font-medium">
                              New Property Alerts
                            </Label>
                            <p className="text-sm text-gray-500">
                              Get notified when agents list new properties
                            </p>
                          </div>
                          <Switch
                            id="newPropertyAlertsEnabled"
                            checked={currentSettings.newPropertyAlertsEnabled}
                            onCheckedChange={(checked) =>
                              updateSetting("newPropertyAlertsEnabled", checked)
                            }
                          />
                        </div>

                        {currentSettings.newPropertyAlertsEnabled && (
                          <div className="border-t pt-4">
                            <Label htmlFor="newPropertyThreshold">Alert me every</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                id="newPropertyThreshold"
                                type="number"
                                min="1"
                                max="20"
                                value={currentSettings.newPropertyThreshold}
                                onChange={(e) =>
                                  updateSetting("newPropertyThreshold", Number.parseInt(e.target.value))
                                }
                                className="w-20"
                              />
                              <span className="text-sm text-gray-600">new properties</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="propertyInquiries" className="font-medium">
                              Property Inquiries
                            </Label>
                            <p className="text-sm text-gray-500">
                              When prospects inquire about any property
                            </p>
                          </div>
                          <Switch
                            id="propertyInquiries"
                            checked={currentSettings.propertyInquiries}
                            onCheckedChange={(checked) =>
                              updateSetting("propertyInquiries", checked)
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="listingExpirations" className="font-medium">
                              Listing Expirations
                            </Label>
                            <p className="text-sm text-gray-500">
                              7 days before listing expires
                            </p>
                          </div>
                          <Switch
                            id="listingExpirations"
                            checked={currentSettings.listingExpirations}
                            onCheckedChange={(checked) =>
                              updateSetting("listingExpirations", checked)
                            }
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Agent Settings */}
              {activeRole === "agent" && (
                <>
                  {/* Lead Management */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#55C5D0]" />
                        <CardTitle>Lead Management</CardTitle>
                      </div>
                      <CardDescription>
                        Configure your lead-related notifications
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="newLeadAssigned" className="font-medium">
                              New Lead Assigned
                            </Label>
                            <p className="text-sm text-gray-500">
                              Immediate notification when any lead is assigned to you
                            </p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="orange">High Priority</Badge>
                            </div>
                          </div>
                          <Switch
                            id="newLeadAssigned"
                            checked={currentSettings.newLeadAssigned}
                            onCheckedChange={(checked) =>
                              updateSetting("newLeadAssigned", checked)
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="hotLeadPriorityAlert" className="font-medium">
                              Hot Lead Priority Alert
                            </Label>
                            <p className="text-sm text-gray-500">
                              When assigned lead is classified as "Hot" by AI dialer
                            </p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="destructive">Critical</Badge>
                            </div>
                          </div>
                          <Switch
                            id="hotLeadPriorityAlert"
                            checked={currentSettings.hotLeadPriorityAlert}
                            onCheckedChange={(checked) =>
                              updateSetting("hotLeadPriorityAlert", checked)
                            }
                          />
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <Label htmlFor="followUpReminders">Follow-up Reminders</Label>
                        <Select
                          value={currentSettings.followUpReminders}
                          onValueChange={(value) => updateSetting("followUpReminders", value)}
                        >
                          <SelectTrigger className="mt-1 max-w-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="disabled">Disabled</SelectItem>
                            <SelectItem value="6 hours">After 6 hours</SelectItem>
                            <SelectItem value="12 hours">After 12 hours</SelectItem>
                            <SelectItem value="24 hours">After 24 hours</SelectItem>
                            <SelectItem value="48 hours">After 48 hours</SelectItem>
                            <SelectItem value="72 hours">After 72 hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Property Management */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Building className="w-5 h-5 text-[#55C5D0]" />
                        <CardTitle>Property Management</CardTitle>
                      </div>
                      <CardDescription>
                        Configure property-related notifications
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="propertyInquiryReceived" className="font-medium">
                            Property Enquiry Received
                          </Label>
                          <p className="text-sm text-gray-500">
                            When prospects inquire about your listings
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="orange">High Priority</Badge>
                          </div>
                        </div>
                        <Switch
                          id="propertyInquiryReceived"
                          checked={currentSettings.propertyInquiryReceived}
                          onCheckedChange={(checked) =>
                            updateSetting("propertyInquiryReceived", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="virtualTourRequests" className="font-medium">
                            Virtual Tour Requests
                          </Label>
                          <p className="text-sm text-gray-500">
                            When prospects request property tours
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="orange">High Priority</Badge>
                          </div>
                        </div>
                        <Switch
                          id="virtualTourRequests"
                          checked={currentSettings.virtualTourRequests}
                          onCheckedChange={(checked) =>
                            updateSetting("virtualTourRequests", checked)
                          }
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Communication */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Phone className="w-5 h-5 text-[#55C5D0]" />
                        <CardTitle>Communication</CardTitle>
                      </div>
                      <CardDescription>
                        Configure communication notifications
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="incomingCallTransfers" className="font-medium">
                            Incoming Call Transfers
                          </Label>
                          <p className="text-sm text-gray-500">
                            When AI dialer transfers prospect to you
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="destructive">Critical</Badge>
                          </div>
                        </div>
                        <Switch
                          id="incomingCallTransfers"
                          checked={currentSettings.incomingCallTransfers}
                          onCheckedChange={(checked) =>
                            updateSetting("incomingCallTransfers", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="chatRequests" className="font-medium">
                            Chat Requests
                          </Label>
                          <p className="text-sm text-gray-500">
                            When website visitors request live chat
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="orange">High Priority</Badge>
                          </div>
                        </div>
                        <Switch
                          id="chatRequests"
                          checked={currentSettings.chatRequests}
                          onCheckedChange={(checked) =>
                            updateSetting("chatRequests", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="callTranscriptsReady" className="font-medium">
                            Call Transcripts Ready
                          </Label>
                          <p className="text-sm text-gray-500">
                            When AI processes call transcripts
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline">Low Priority</Badge>
                          </div>
                        </div>
                        <Switch
                          id="callTranscriptsReady"
                          checked={currentSettings.callTranscriptsReady}
                          onCheckedChange={(checked) =>
                            updateSetting("callTranscriptsReady", checked)
                          }
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* System & Account */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <SettingsIcon className="w-5 h-5 text-gray-600" />
                        <CardTitle>System & Account</CardTitle>
                      </div>
                      <CardDescription>
                        Configure account and system notifications
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="profileUpdateReminders">Profile Update Reminders</Label>
                        <Select
                          value={currentSettings.profileUpdateReminders}
                          onValueChange={(value) => updateSetting("profileUpdateReminders", value)}
                        >
                          <SelectTrigger className="mt-1 max-w-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="disabled">Disabled</SelectItem>
                            <SelectItem value="monthly">Monthly reminders</SelectItem>
                            <SelectItem value="quarterly">Quarterly reminders</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="trainingNotifications" className="font-medium">
                            Training Notifications
                          </Label>
                          <p className="text-sm text-gray-500">
                            New training materials available
                          </p>
                        </div>
                        <Switch
                          id="trainingNotifications"
                          checked={currentSettings.trainingNotifications}
                          onCheckedChange={(checked) =>
                            updateSetting("trainingNotifications", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="performanceReviews" className="font-medium">
                            Performance Reviews
                          </Label>
                          <p className="text-sm text-gray-500">
                            Monthly performance summaries
                          </p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="secondary">Email only</Badge>
                          </div>
                        </div>
                        <Switch
                          id="performanceReviews"
                          checked={currentSettings.performanceReviews}
                          onCheckedChange={(checked) =>
                            updateSetting("performanceReviews", checked)
                          }
                        />
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* General Settings - Same for both roles */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-indigo-600" />
                    <CardTitle>General Settings</CardTitle>
                  </div>
                  <CardDescription>
                    Configure notification delivery channels and preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">
                      Notification Delivery Channels
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <div>
                            <Label htmlFor="emailNotifications" className="font-medium">
                              Email Notifications
                            </Label>
                            <p className="text-sm text-gray-500">
                              Professional email notifications with detailed information
                            </p>
                          </div>
                        </div>
                        <Switch
                          id="emailNotifications"
                          checked={generalSettings.emailNotifications}
                          onCheckedChange={(checked) =>
                            updateGeneralSetting("emailNotifications", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Monitor className="w-4 h-4 text-gray-500" />
                          <div>
                            <Label htmlFor="inAppNotifications" className="font-medium">
                              In-App Notifications
                            </Label>
                            <p className="text-sm text-gray-500">
                              Dashboard badge notifications and popup alerts
                            </p>
                          </div>
                        </div>
                        <Switch
                          id="inAppNotifications"
                          checked={generalSettings.inAppNotifications}
                          onCheckedChange={(checked) =>
                            updateGeneralSetting("inAppNotifications", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Smartphone className="w-4 h-4 text-gray-500" />
                          <div>
                            <Label htmlFor="smsNotifications" className="font-medium">
                              SMS Notifications
                            </Label>
                            <p className="text-sm text-gray-500">
                              Reserved for critical alerts only
                            </p>
                          </div>
                        </div>
                        <Switch
                          id="smsNotifications"
                          checked={generalSettings.smsNotifications}
                          onCheckedChange={(checked) =>
                            updateGeneralSetting("smsNotifications", checked)
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Bell className="w-4 h-4 text-gray-500" />
                          <div>
                            <Label htmlFor="pushNotifications" className="font-medium">
                              Push Notifications
                            </Label>
                            <p className="text-sm text-gray-500">
                              Browser/mobile push with quick actions
                            </p>
                          </div>
                        </div>
                        <Switch
                          id="pushNotifications"
                          checked={generalSettings.pushNotifications}
                          onCheckedChange={(checked) =>
                            updateGeneralSetting("pushNotifications", checked)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Quiet Hours
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Non-urgent notifications are queued during quiet hours.
                      Critical alerts bypass this setting.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="quietHoursFrom">From Time</Label>
                        <Input
                          id="quietHoursFrom"
                          type="time"
                          value={generalSettings.quietHoursFrom}
                          onChange={(e) =>
                            updateGeneralSetting("quietHoursFrom", e.target.value)
                          }
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="quietHoursTo">To Time</Label>
                        <Input
                          id="quietHoursTo"
                          type="time"
                          value={generalSettings.quietHoursTo}
                          onChange={(e) =>
                            updateGeneralSetting("quietHoursTo", e.target.value)
                          }
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Portal Link Tab (all roles) */}
          {activeTab === "portal-link" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-[#55C5D0]" />
                    <CardTitle>Your Buyer Portal Sign-up Link</CardTitle>
                  </div>
                  <CardDescription>
                    Share this personal link (or its QR code) with anyone you'd like to invite to the
                    buyer portal. Every account created from it is automatically credited to you in
                    Analytics â†’ Traffic Sources â†’ Real Estate Agents.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <Label htmlFor="portalSignupUrl">Your unique link</Label>
                    <div className="flex flex-col sm:flex-row gap-2 mt-1">
                      <Input
                        id="portalSignupUrl"
                        readOnly
                        value={portalSignupUrl}
                        onClick={(e) => e.target.select()}
                        className="flex-1 font-mono text-xs sm:text-sm"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={copyPortalUrl}
                          className="flex-1 sm:flex-none"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </Button>
                        <a
                          href={portalSignupUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50 flex-1 sm:flex-none"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open
                        </a>
                      </div>
                    </div>
                  </div>

                  {!user?.id && (
                    <p className="text-xs text-amber-600">
                      Could not detect your agent ID â€” sign-ups from this link won't be attributed to
                      you. Please log out and back in.
                    </p>
                  )}

                  <Separator />

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h4 className="font-medium text-gray-900">QR code</h4>
                      <p className="text-sm text-gray-500">
                        Display or print a QR code for open homes, your desk, or email signatures.
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => setShowPortalQr(true)}
                      className="bg-[#55C5D0] hover:bg-[#3bb3be] text-white"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Show QR code
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Chatbot Tab (Admin only) */}
          {activeTab === "chatbot" && activeRole === "admin" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#55C5D0]" />
                    Website Chat Assistant
                  </CardTitle>
                  <CardDescription>
                    Configure the AI chatbot for website visitors (not phone calling)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <Label htmlFor="chatbot_name">Chatbot Name</Label>
                      <Input
                        id="chatbot_name"
                        value={chatbotSettings.chatbot_name}
                        onChange={(e) => setChatbotSettings({ ...chatbotSettings, chatbot_name: e.target.value })}
                        placeholder="e.g., Alex"
                      />
                    </div>

                    <div>
                      <Label>Enable Chatbot</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <Switch
                          checked={chatbotSettings.is_active}
                          onCheckedChange={(checked) => setChatbotSettings({ ...chatbotSettings, is_active: checked })}
                        />
                        <span className="text-sm text-gray-600">
                          {chatbotSettings.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="welcome_message">Welcome Message</Label>
                    <textarea
                      id="welcome_message"
                      value={chatbotSettings.welcome_message}
                      onChange={(e) => setChatbotSettings({ ...chatbotSettings, welcome_message: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                    />
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Agent Availability Hours</h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Live chat is available only during these hours. Outside these hours, chatbot runs automatically.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label htmlFor="agent_availability_start">Start Time</Label>
                        <Input
                          id="agent_availability_start"
                          type="time"
                          value={chatbotSettings.agent_availability_start}
                          onChange={(e) => setChatbotSettings({ ...chatbotSettings, agent_availability_start: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="agent_availability_end">End Time</Label>
                        <Input
                          id="agent_availability_end"
                          type="time"
                          value={chatbotSettings.agent_availability_end}
                          onChange={(e) => setChatbotSettings({ ...chatbotSettings, agent_availability_end: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Auto-Qualify Leads</Label>
                        <p className="text-sm text-gray-500">
                          Automatically qualify buyers and sellers through conversation
                        </p>
                      </div>
                      <Switch
                        checked={chatbotSettings.auto_qualify_leads}
                        onCheckedChange={(checked) => setChatbotSettings({ ...chatbotSettings, auto_qualify_leads: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Offer Calendar Booking</Label>
                        <p className="text-sm text-gray-500">
                          Allow users to book property inspections through chat
                        </p>
                      </div>
                      <Switch
                        checked={chatbotSettings.offer_calendar_booking}
                        onCheckedChange={(checked) => setChatbotSettings({ ...chatbotSettings, offer_calendar_booking: checked })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Email Tab (Admin only) */}
          {activeTab === "email" && activeRole === "admin" && (
            <EmailSettings />
          )}

          {/* Voice Assistant Tab */}
          {activeTab === "voice-assistant" && activeRole === "admin" && (
            <VoiceAssistantSettings />
          )}

          {/* My Assistant (PA) Tab â€” open to every CRM user */}
          {activeTab === "my-assistant" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#55C5D0]" />
                    <CardTitle>Personal Assistants (PA)</CardTitle>
                  </div>
                  <CardDescription>
                    A PA can manage properties on your behalf. You can link up to {paMax},
                    and each one gets their own access level.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {paList === null ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0]"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {paList.length === 0 && !paFormVisible && (
                        <div className="text-center py-8">
                          <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 mb-4">No Personal Assistant linked yet.</p>
                          <button
                            onClick={() => setPaFormVisible(true)}
                            className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] transition-colors font-medium"
                          >
                            <UserPlus className="w-4 h-4" />
                            Invite Personal Assistant
                          </button>
                        </div>
                      )}

                      {paList.map((pa) => (
                        <div key={pa.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="flex items-center gap-4 p-4 bg-green-50 border-b border-green-200">
                            <div className="w-12 h-12 rounded-full bg-[#55C5D0] flex items-center justify-center text-white font-bold text-xl">
                              {pa.name?.[0]?.toUpperCase() || 'P'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{pa.name}</p>
                              <p className="text-sm text-gray-500 truncate">{pa.email}</p>
                              {/* A colleague who has been asked but has not
                                  answered is an ACTIVE agent, so is_active alone
                                  would call them an active assistant. The server
                                  says which state this row is actually in. */}
                              {(() => {
                                const waiting = pa.assistant_status
                                  ? pa.assistant_status === 'pending'
                                  : !pa.is_active;
                                return (
                                  <span className={`inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${waiting ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                                    }`}>
                                    {waiting
                                      ? (pa.assistant_kind === 'colleague'
                                        ? 'Waiting for them to accept'
                                        : 'Invite pending')
                                      : 'Active'}
                                  </span>
                                );
                              })()}
                            </div>
                            <button
                              onClick={() => setPaPendingDelete(pa)}
                              disabled={paRemovingId === pa.id}
                              className="flex items-center gap-1 px-3 py-2 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              {paRemovingId === pa.id ? 'Removing...' : 'Remove'}
                            </button>
                          </div>

                          {/* Access level is per assistant, not one setting for all of them */}
                          <div className="p-4 bg-gray-50">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900">Access Level</p>
                                <p className="text-xs text-gray-500 mt-0.5">Define what this PA can access in the CRM.</p>
                              </div>
                              <select
                                value={pa.pa_access_level || 'full_access'}
                                onChange={(e) => handlePaAccessLevel(pa.id, e.target.value)}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0] cursor-pointer"
                              >
                                <option value="properties_only">Properties Only &ndash; View, Add &amp; Edit properties</option>
                                <option value="full_access">Full Access &ndash; Everything you can do</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Add another, until the cap is reached */}
                      {paList.length > 0 && !paFormVisible && (
                        paList.length < paMax ? (
                          <button
                            onClick={() => setPaFormVisible(true)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-[#55C5D0] border border-dashed border-[#55C5D0] rounded-lg hover:bg-[#55C5D0]/5 transition-colors font-medium w-full justify-center"
                          >
                            <UserPlus className="w-4 h-4" />
                            Add another Personal Assistant
                          </button>
                        ) : (
                          <p className="text-xs text-gray-500 text-center">
                            You have reached the maximum of {paMax} Personal Assistants.
                            Remove one to add another.
                          </p>
                        )
                      )}

                      {paFormVisible && (
                        <div className="space-y-4 border border-gray-200 rounded-lg p-4">
                          <h3 className="font-medium text-gray-800">Add a Personal Assistant</h3>

                          {/* Two ways in. Picking a colleague is the default: an
                              agent who assists keeps their own login and simply
                              takes on the extra job. Typing a colleague's email
                              used to overwrite their account (Monday CRM-2026-00028). */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setPaMode('team')}
                              className={`px-4 py-2 text-sm rounded-lg border transition-colors ${paMode === 'team'
                                ? 'bg-[#55C5D0] text-white border-[#55C5D0]'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            >
                              Someone from the team
                            </button>
                            <button
                              type="button"
                              onClick={() => setPaMode('new')}
                              className={`px-4 py-2 text-sm rounded-lg border transition-colors ${paMode === 'new'
                                ? 'bg-[#55C5D0] text-white border-[#55C5D0]'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            >
                              Someone new
                            </button>
                          </div>

                          {paMode === 'team' ? (
                            <div className="space-y-4">
                              <p className="text-sm text-gray-500">
                                Choose a colleague who already works here. They keep their own
                                login and everything they have today, and they are emailed an
                                invitation to accept before anything changes.
                              </p>
                              <div>
                                <Label htmlFor="pa-colleague">Team Member <span className="text-red-500">*</span></Label>
                                <select
                                  id="pa-colleague"
                                  value={paPickedId}
                                  onChange={e => setPaPickedId(e.target.value)}
                                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                >
                                  <option value="">Select a team member...</option>
                                  {paCandidates.map((c) => (
                                    <option key={c.id} value={c.id} disabled={c.already_invited}>
                                      {c.name} ({c.email})
                                      {c.already_invited ? ' - already invited' : ''}
                                    </option>
                                  ))}
                                </select>
                                {paCandidates.length === 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Nobody is available to add. Everyone else is already
                                    assisting somebody, or has no CRM login yet - use
                                    "Someone new" for those.
                                  </p>
                                )}
                              </div>
                              <div>
                                <Label htmlFor="pa-access-new">Access Level</Label>
                                <select
                                  id="pa-access-new"
                                  value={paAccessLevel}
                                  onChange={e => setPaAccessLevel(e.target.value)}
                                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                >
                                  <option value="properties_only">Properties Only &ndash; View, Add &amp; Edit properties</option>
                                  <option value="full_access">Full Access &ndash; Everything you can do</option>
                                </select>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <p className="text-sm text-gray-500">
                                For an assistant with no CRM login yet. They will receive an
                                email to set up their account.
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="pa-name">Full Name <span className="text-red-500">*</span></Label>
                                  <input
                                    id="pa-name"
                                    type="text"
                                    value={paForm.name}
                                    onChange={e => setPaForm(p => ({ ...p, name: e.target.value }))}
                                    placeholder="e.g. Marisa Smith"
                                    className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                    required
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="pa-email">Email Address <span className="text-red-500">*</span></Label>
                                  <input
                                    id="pa-email"
                                    type="email"
                                    value={paForm.email}
                                    onChange={e => setPaForm(p => ({ ...p, email: e.target.value }))}
                                    placeholder="e.g. marisa@collings.com.au"
                                    className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                    required
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <Label htmlFor="pa-phone">Phone Number</Label>
                                  <input
                                    id="pa-phone"
                                    type="text"
                                    value={paForm.phone_number}
                                    onChange={e => setPaForm(p => ({ ...p, phone_number: e.target.value }))}
                                    placeholder="+61 4XX XXX XXX"
                                    className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex justify-end gap-3 pt-2">
                            <button
                              onClick={() => {
                                setPaFormVisible(false);
                                setPaForm({ name: '', email: '', phone_number: '' });
                                setPaPickedId('');
                              }}
                              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={async () => {
                                const pickingColleague = paMode === 'team';
                                if (pickingColleague && !paPickedId) {
                                  toast.error("Choose a team member.");
                                  return;
                                }
                                if (!pickingColleague && (!paForm.name || !paForm.email)) {
                                  toast.error("Name and email are required.");
                                  return;
                                }
                                setPaLoading(true);
                                try {
                                  if (pickingColleague) {
                                    await inviteAssistantById(Number(paPickedId), paAccessLevel);
                                    toast.success("Invitation sent. They will appear here once they accept.");
                                  } else {
                                    await inviteAssistant({ ...paForm, pa_access_level: paAccessLevel });
                                    toast.success("Invitation sent! Your PA will receive a setup email.");
                                  }
                                  // Re-read rather than push the one row back:
                                  // a colleague comes back as "pending" and the
                                  // dropdown has to drop them.
                                  await refreshAssistants();
                                  setPaFormVisible(false);
                                  setPaForm({ name: '', email: '', phone_number: '' });
                                  setPaPickedId('');
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Failed to invite assistant.");
                                } finally {
                                  setPaLoading(false);
                                }
                              }}
                              disabled={paLoading}
                              className="flex items-center gap-2 px-5 py-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] transition-colors font-medium disabled:opacity-50"
                            >
                              <UserPlus className="w-4 h-4" />
                              {paLoading ? 'Sending...' : 'Send Invitation'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Security Tab (all roles) - two-factor authentication */}
          {activeTab === "security" && (
            <div className="space-y-6">
              <TwoFactorSettings />
            </div>
          )}

          {/* SMS Senders Tab (Super admin only) */}
          {activeTab === "sms-senders" && canManageSmsSenders && (
            <div className="space-y-6">
              <SmsSendersSettings />
            </div>
          )}

          {/* Save Button (hide for email, voice-assistant, my-assistant, portal-link, security and sms-senders tabs which have no settings to save) */}
          {activeTab !== "email" && activeTab !== "voice-assistant" && activeTab !== "my-assistant" && activeTab !== "portal-link" && activeTab !== "security" && activeTab !== "sms-senders" && (
            <div className="flex justify-end pt-6">
              <Button
                size="lg"
                className="px-8 bg-[#55C5D0] hover:bg-[#3bb3be] text-white"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={Boolean(paPendingDelete)}
        onClose={() => setPaPendingDelete(null)}
        onConfirm={handleDeletePA}
        title="Remove Personal Assistant"
        description="Are you sure you want to remove this Personal Assistant? They will lose access immediately. Their account is archived rather than destroyed, so you can invite them back on the same email address."
        itemName={paPendingDelete?.name}
        loading={paRemovingId === paPendingDelete?.id}
      />
      {showPortalQr && (
        <AgentPortalQrModal agent={user} onClose={() => setShowPortalQr(false)} />
      )}
    </div>
  );
};

export default Settings;
